import { AuthGuard, copyToClipboard } from "@elit-hair/elit-tools";
import { Skeleton, Spinner, Tabs, TabsContent, TabsList, TabsTrigger } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import {
    TRANSCRIPT_VIEW_MODE_SEARCH_VALUES,
    TranscriptViewMode,
    transcriptViewModeFromSearch,
    transcriptViewModeToSearch,
} from "@/constants/transcripts";
import { useSegments } from "@/hooks/use-segments";
import { CallActionsSection } from "@/pages/calls/$callId/components/call-actions-section";
import { CallAnalysisPanel } from "@/pages/calls/$callId/components/call-analysis/call-analysis-panel";
import { CallInfoSection } from "@/pages/calls/$callId/components/call-info-section";
import { GenerateTranscriptButton } from "@/pages/calls/$callId/components/generate-transcript-button";
import { SalesSummarySection } from "@/pages/calls/$callId/components/sales-summary-section";
import { TranscriptContent } from "@/pages/calls/$callId/components/transcript-content";
import { TranscriptDimensions } from "@/pages/calls/$callId/components/transcript-dimensions";
import { TranscriptHeader } from "@/pages/calls/$callId/components/transcript-header";
import { TranscriptTabs } from "@/pages/calls/$callId/components/transcript-tabs";
import { AudioPlayer } from "@/pages/calls/components/audio-player";
import { useTranscriptData } from "@/pages/calls/hooks/use-transcript";
import { NotAuthorizedPage } from "@/pages/not-authorized/-index";
import { useGetCall } from "@/queries/calls/calls.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { formatTranscriptForExport } from "@/utils/segmentUtils";
import { searchValidator } from "@/utils/search-validator";

import { TranscriptNavigationProvider } from "./contexts/transcript-navigation.context";

const CALL_DETAILS_TABS = ["transcript", "details", "evaluation"] as const;
type CallDetailsTab = (typeof CALL_DETAILS_TABS)[number];

const CallDetailsSearchSchema = z.object({
    tab: z.enum(CALL_DETAILS_TABS).optional(),
    "view-mode": z.enum(TRANSCRIPT_VIEW_MODE_SEARCH_VALUES).optional(),
});

const isCallDetailsTab = (tab: string): tab is CallDetailsTab => {
    return CALL_DETAILS_TABS.includes(tab as CallDetailsTab);
};

export const Route = createFileRoute(APP_ROUTES_FULL.CALL_DETAILS)({
    component: CallDetails,
    validateSearch: searchValidator(CallDetailsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "call_details",
                to: APP_ROUTES_TO.CALL_DETAILS,
            },
            title: "call_details",
        },
    },
});

function CallDetails() {
    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const callId = params.callId;

    const { data: call, isLoading: isLoadingCall } = useGetCall(callId);

    const viewMode = transcriptViewModeFromSearch(search["view-mode"]);

    const {
        transcripts,
        selectedTranscript,
        groupedTranscripts,
        isLoading: isLoadingTranscripts,
        setSelectedTranscript,
        refetchTranscripts,
        deleteTranscript,
    } = useTranscriptData(callId);

    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const activeTab = search.tab ?? "transcript";

    const { segments, isLoadingSegments } = useSegments(selectedTranscript, viewMode, 0.6);

    const handleCopyTranscript = async () => {
        const text = formatTranscriptForExport(selectedTranscript?.segments);
        if (!text) {
            toast.error(t("no_transcript_content_available_yet"));
            return;
        }

        try {
            await copyToClipboard(text);
            toast.success(t("copied_to_clipboard"));
        } catch (error) {
            console.error("Failed to copy transcript to clipboard:", error);
            toast.error(t("failed_to_copy_transcript"));
        }
    };

    const handleDeleteTranscript = async (transcriptId: string) => {
        try {
            await deleteTranscript(transcriptId);
        } catch (err) {
            console.error("Failed to delete transcript:", err);
        }
    };

    const handleDownloadTranscript = () => {
        if (!selectedTranscript) {
            toast.error(t("no_transcript_content_available_yet"));
            return;
        }

        const text = formatTranscriptForExport(selectedTranscript.segments);
        if (!text) {
            toast.error(t("no_transcript_content_available_yet"));
            return;
        }

        const blob = new Blob([text], {
            type: "text/plain",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transcript-${callId}-${selectedTranscript.id}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleTabChange = (tab: string) => {
        if (!isCallDetailsTab(tab) || tab === activeTab) return;

        navigate({
            search: (old) => ({
                ...old,
                tab,
            }),
            replace: true,
        });
    };

    const handleViewModeChange = (mode: TranscriptViewMode) => {
        if (mode === viewMode) return;

        navigate({
            search: (old) => ({
                ...old,
                "view-mode": transcriptViewModeToSearch(mode),
            }),
            replace: true,
        });
    };

    const hasTranscript = transcripts.length > 0;
    const transcript = selectedTranscript?.transcriptText || "";
    const canGenerateTranscript = Boolean(call?.callRecordingUrl);

    if (!callId) {
        return (
            <div className="flex flex-col gap-4 h-full">
                <div className="flex flex-col items-center justify-center flex-1 space-y-4">
                    <h2 className="text-xl font-medium">{t("invalid_call_id")}</h2>
                    <p className="text-muted-foreground">{t("invalid_call_id_description")}</p>
                </div>
            </div>
        );
    }

    return (
        <TranscriptNavigationProvider>
            <div className="sticky top-0 z-sticky rounded-lg mb-4 bg-background/95 backdrop-blur-sm">
                {isLoadingCall ? (
                    <Skeleton className="w-full h-24" />
                ) : call?.callRecordingUrl ? (
                    <AudioPlayer
                        url={call.callRecordingUrl}
                        callId={callId}
                        isPlaying={isPlaying}
                        setIsPlaying={setIsPlaying}
                        currentTime={currentTime}
                        setCurrentTime={setCurrentTime}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-24 space-y-4 w-full">
                        <p className="text-muted-foreground text-lg">{t("no_recording_available")}</p>
                    </div>
                )}
            </div>
            <div className="flex-1 overflow-hidden">
                <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
                    <TabsList className="flex h-auto w-full gap-1 sm:gap-2">
                        <TabsTrigger value="transcript" className="flex-1 px-2 text-xs sm:px-3 sm:text-sm">
                            {t("transcript")}
                        </TabsTrigger>
                        <TabsTrigger value="details" className="flex-1 px-2 text-xs sm:px-3 sm:text-sm">
                            {t("call_details")}
                        </TabsTrigger>
                        <AuthGuard required={[PERMISSIONS.CALL_CALL_EVALUATION]}>
                            <TabsTrigger value="evaluation" className="flex-1 px-2 text-xs sm:px-3 sm:text-sm">
                                {t("evaluation")}
                            </TabsTrigger>
                        </AuthGuard>
                    </TabsList>

                    <TabsContent
                        value="transcript"
                        className="mt-4 flex flex-1 flex-col gap-4 overflow-auto px-0 sm:px-2 lg:flex-row lg:gap-2"
                    >
                        <div className="min-w-0 w-full p-2">
                            {isLoadingTranscripts ? (
                                <div className="flex flex-col items-center justify-center h-full space-y-4">
                                    <Spinner size="lg" />
                                    <p className="text-muted-foreground text-lg">{t("loading_transcript")}</p>
                                </div>
                            ) : hasTranscript ? (
                                <div className="relative space-y-6">
                                    <TranscriptTabs
                                        callId={callId}
                                        groupedTranscripts={groupedTranscripts}
                                        selectedTranscript={selectedTranscript}
                                        onSelectTranscript={setSelectedTranscript}
                                        canGenerateTranscript={canGenerateTranscript}
                                    />

                                    <TranscriptHeader
                                        selectedTranscript={selectedTranscript}
                                        viewMode={viewMode}
                                        onSetViewMode={handleViewModeChange}
                                        onLoadSegments={refetchTranscripts}
                                        onDeleteTranscript={handleDeleteTranscript}
                                        onDownloadTranscript={handleDownloadTranscript}
                                        onCopyTranscript={handleCopyTranscript}
                                    />

                                    <TranscriptContent
                                        viewMode={viewMode}
                                        transcript={transcript}
                                        segments={segments}
                                        isLoadingSegments={isLoadingSegments}
                                        currentTime={currentTime}
                                        setCurrentTime={setCurrentTime}
                                        selectedTranscript={selectedTranscript}
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full space-y-6 max-w-md mx-auto text-center w-full px-2">
                                    <div className="space-y-3 w-full">
                                        <h2 className="text-xl font-medium sm:text-2xl">
                                            {t("no_transcripts_available")}
                                        </h2>
                                        <p className="text-muted-foreground">
                                            {t("transcript_may_still_be_processing")}
                                        </p>
                                    </div>
                                    <GenerateTranscriptButton callId={callId} disabled={!canGenerateTranscript} />
                                    {!canGenerateTranscript && (
                                        <p className="text-sm text-muted-foreground">{t("no_recording_available")}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {hasTranscript && (
                            <div className="w-full shrink-0 rounded-lg border border-border p-2 shadow-md lg:sticky lg:top-0 lg:h-full lg:w-[300px] lg:overflow-x-hidden lg:overflow-y-auto">
                                <TranscriptDimensions
                                    transcriptId={selectedTranscript?.id}
                                    selectedTranscript={selectedTranscript}
                                    onDimensionClick={() => {}}
                                    transcript={transcript}
                                />
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="details" className="mt-4 flex-1 overflow-auto">
                        {isLoadingCall ? (
                            <div className="flex justify-center items-center h-full">
                                <Spinner size="lg" />
                            </div>
                        ) : call ? (
                            <div className="p-3 text-sm sm:p-6">
                                <div className="flex flex-col gap-6">
                                    <CallInfoSection call={call} />
                                    <CallActionsSection call={call} />
                                    <SalesSummarySection transcriptId={selectedTranscript?.id} />
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-center items-center h-full">
                                <p className="text-muted-foreground">{t("call_not_found")}</p>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="evaluation" className="flex-1 overflow-auto mt-4">
                        <AuthGuard required={[PERMISSIONS.CALL_CALL_EVALUATION]} fallback={<NotAuthorizedPage />}>
                            <CallAnalysisPanel
                                callId={callId}
                                transcripts={transcripts}
                                isLoadingTranscripts={isLoadingTranscripts}
                                selectedTranscript={selectedTranscript ?? undefined}
                                onSelectTranscript={setSelectedTranscript}
                                canGenerateTranscript={canGenerateTranscript}
                            />
                        </AuthGuard>
                    </TabsContent>
                </Tabs>
            </div>
        </TranscriptNavigationProvider>
    );
}
